import { ChatRequestError, toChatError, type ChatError } from './errors';
import { HttpClient, normalizeExpiry, type MintedToken, type Snapshot } from './http';
import { isWhatsAppUrl, parseExtras, type ChatExtras, type VisitorInfo } from './extras';
import { InteractionController } from './interactions';
import { browserPage, StreamConnection, type PageSignals } from './connection';
import { readInteraction, readWelcome, readWireMessage } from './protocol/readers';
import { SUPPORTED_INTERACTIONS, type InteractionMessage, type InteractionResponse, type WireEnvelope, type WriteResult } from './protocol/types';
import type { Divider, Message, Reaction, TranscriptItem, Welcome } from './state';
import { browserStore, getVisitorId, sessionHintKey, type KeyValueStore } from './storage';
import { SDK_VERSION } from './version';

export const DEFAULT_BASE_URL = 'https://api.osvi.ai';
export const AWAITING_REPLY_TIMEOUT_MS = 120_000;
const SUPPORT_TYPING_MS = 6_000;
export const SEND_ACK_TIMEOUT_MS = 15_000;
const BUSY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];
const MAX_LIMIT_RETRIES = 3;
const DEFAULT_RATE_WAIT_S = 5;
// Limit notices a reconnect must not clear: the server would refuse the next message again.
const STICKY_ON_RECONNECT = new Set(['daily_limit_reached', 'session_limit_reached']);
const DEFAULT_DAILY_WAIT_S = 300;   // also the cap on the composer lock: the owner may raise the limit meanwhile
const SEEN_EVENT_CAP = 2048;
const QUIET_END_REASONS = new Set(['session_closed', 'session_dormant', 'new_session']);

export interface ServerToken { sessionId: string; token: string; wsUrl: string; expiresAt: number }
export type ChatAuth = { mode: 'anonymous' } | { mode: 'server'; getToken: (opts?: { newSession?: boolean }) => Promise<ServerToken> };
export interface ChatClientOptions {
  agentId: string; baseUrl?: string; auth?: ChatAuth;
  initialAgent?: { name: string; welcome?: unknown };   // from the widget's page-load probe
  initialExtras?: ChatExtras;                          // likewise: appearance, pre-chat, hand-off
  fetch?: typeof fetch; WebSocketImpl?: typeof WebSocket; store?: KeyValueStore; page?: PageSignals; random?: () => number;
}
export type ChatStatus = 'idle' | 'connecting' | 'open' | 'recovering' | 'offline' | 'closed';
export interface ChatState {
  status: ChatStatus;
  mode: 'ai' | 'human';
  awaitingReply: boolean;
  /** A support person is typing; transient (auto-clears after 6 s), never persisted. */
  supportTyping?: boolean;
  items: TranscriptItem[];
  activeInteraction: InteractionMessage | null;
  interactionPending: boolean;
  agent: { agentId: string; name: string } | null;
  error: ChatError | null;
  /** Server-driven widget extras; absent on older servers and accounts without channel extras. */
  extras?: ChatExtras;
  /** Pre-chat answers, kept in this browser only (the server has no field for them). */
  visitor?: VisitorInfo;
}
export type PrechatOutcome = { ok: true } | { ok: false; reason: 'invalid' | 'rate_limited' | 'network' | 'unavailable' };
export type HandoffOutcome = { ok: true; url: string } | { ok: false; reason: 'unavailable' | 'rate_limited' | 'network' };
export type SendOutcome = { ok: true; messageId: string } | { ok: false; reason: 'empty' | 'destroyed' | 'awaiting_interaction' | 'unavailable' };
export interface ChatClient {
  start(): Promise<void>;
  send(text: string): Promise<SendOutcome>;
  respond(interactionId: string, response: InteractionResponse): Promise<boolean>;
  retry(messageId: string): Promise<boolean>;
  newSession(opts?: { keepHistory?: boolean }): Promise<boolean>;   // keepHistory defaults to true
  /** Optional so hand-written clients keep compiling; the UI shows no pre-chat gate / hand-off button without them. */
  submitPrechat?(info: VisitorInfo): Promise<PrechatOutcome>;
  whatsappHandoff?(): Promise<HandoffOutcome>;
  subscribe(listener: (state: ChatState) => void): () => void;
  getState(): ChatState;
  resolveUrl(path: string): string;
  destroy(): void;
}

interface PendingSend {
  frame: Record<string, unknown>;
  attempts: number;
  gen: number;                                        // socket generation this frame was last written on (-1: never)
  limited?: number;                                   // rate_limited rejections so far
  ackTimer: ReturnType<typeof setTimeout> | null;
}

// Message ids and idempotency keys are not secrets, so the last-resort fallback is allowed here.
// Anything security-relevant (the visitor id, D4) never falls back to Math.random.
function uuid(): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  if (c?.getRandomValues) {
    const b = c.getRandomValues(new Uint8Array(16));
    b[6] = (b[6]! & 0x0f) | 0x40; b[8] = (b[8]! & 0x3f) | 0x80;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

const emptyState = (): ChatState => ({ status: 'idle', mode: 'ai', awaitingReply: false, items: [], activeInteraction: null,
  interactionPending: false, agent: null, error: null });

const ROLE_MAP: Record<string, Message['role'] | undefined> = {
  user: 'user', ai: 'assistant', assistant: 'assistant', operator: 'human', support: 'human',
};

class Client implements ChatClient {
  private state: ChatState = emptyState();
  private readonly listeners = new Set<(s: ChatState) => void>();
  private readonly http: HttpClient;
  private readonly store: KeyValueStore;
  private readonly page: PageSignals;
  private readonly interactions: InteractionController;
  private conn: StreamConnection | null = null;
  private sessionId: string | null = null;
  private latestToken: string | null = null;
  private chain: Promise<unknown> = Promise.resolve();   // serializes start / ensureSession / newSession
  private startP: Promise<void> | null = null;
  private ensureP: Promise<string | null> | null = null;
  private readonly pending = new Map<string, PendingSend>();   // sends not yet acked/echoed, in send order
  private openGen = 0;
  private sessionGeneration = 0;
  private seen: string[] = [];
  private readonly seenSet = new Set<string>();
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private awaitingTimer: ReturnType<typeof setTimeout> | null = null;
  private supportTypingTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  private welcome: Welcome | null = null;
  private locked = false;   // public chat switched off: nothing more can be sent
  private wantNewServerSession = false;
  private switchTo: ServerToken | null = null;   // server mode: rotation returned a different session

  constructor(private readonly opts: ChatClientOptions) {
    this.http = new HttpClient(opts.baseUrl ?? DEFAULT_BASE_URL, opts.fetch);
    this.store = opts.store ?? browserStore();
    this.page = opts.page ?? browserPage();
    this.interactions = new InteractionController({
      sessionId: () => this.sessionId,
      humanConnected: () => this.state.mode === 'human',
      sendFrame: (frame) => this.conn?.send(frame) ?? false,
      updateItems: (fn) => this.patch({ items: fn(this.state.items) }),
      refreshSnapshot: () => this.fetchSnapshot(),
      onChange: () => this.patch({}),
      newId: uuid,
      limit: (code, wait) => this.showLimit(code, wait),
      clearLimit: () => this.clearLimitNotice('rate_limited'),
      later: (ms, fn) => this.later(ms, fn),
    });
    const visitor = this.readVisitor();
    if (opts.initialExtras || visitor) this.state = { ...this.state, ...(opts.initialExtras ? { extras: opts.initialExtras } : {}), ...(visitor ? { visitor } : {}) };
    if (opts.initialAgent) {
      this.state = { ...this.state, agent: { agentId: opts.agentId, name: opts.initialAgent.name ?? '' } };
      this.applyWelcomeConfig(opts.initialAgent.welcome);
      this.showWelcome();
    }
  }

  private get serverMode(): boolean { return this.opts.auth?.mode === 'server'; }

  private serialize<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.chain.then(fn);
    this.chain = run.catch(() => undefined);
    return run;
  }

  // ---------- public API ----------

  start(): Promise<void> {
    if (this.destroyed) return Promise.resolve();
    if (!this.startP) this.startP = this.serialize(() => this.doStart()).finally(() => { this.startP = null; });
    return this.startP;
  }

  private async doStart(): Promise<void> {
    if (this.destroyed || (this.sessionId && this.conn)) return;
    if (this.serverMode) { await this.openServerSession(); return; }
    try {
      const boot = await this.http.bootstrap(this.opts.agentId, getVisitorId(this.store));
      if (this.destroyed) return;
      this.patch({ agent: { agentId: this.opts.agentId, name: boot.agent?.name ?? '' }, ...this.extrasPatch(boot) });
      this.applyWelcomeConfig(boot.agent?.welcome);
      if (boot.session?.id && boot.session.status === 'active') this.adopt(boot.session.id, boot, this.pending.size ? 'append' : 'replace');
      else { this.store.remove(sessionHintKey(this.opts.agentId)); this.showWelcome(); }
    } catch (e) {
      this.fail(e, false);   // a network blip must not lock the composer
    }
  }

  async send(text: string): Promise<SendOutcome> {
    if (this.destroyed) return { ok: false, reason: 'destroyed' };
    if (this.locked) return { ok: false, reason: 'unavailable' };
    const content = text.trim();
    if (!content) return { ok: false, reason: 'empty' };
    // Text during a pending card supersedes it server-side; only an in-flight card answer blocks.
    if (this.state.mode === 'ai' && this.interactions.pending) {
      return { ok: false, reason: 'awaiting_interaction' };
    }
    const id = uuid();
    const key = uuid();
    const message: Message = { kind: 'message', id, role: 'user', content, createdAt: new Date().toISOString(),
      status: 'sending', idempotencyKey: key };
    this.patch({ items: [...this.state.items, message] });
    this.pending.set(id, { frame: this.frameFor(message), attempts: 0, gen: -1, ackTimer: null });
    const sid = await this.ensureSession();
    if (!sid) {
      this.dropPending(id);
      this.setMessage(id, { status: 'failed' });
      return { ok: false, reason: 'unavailable' };
    }
    this.transmit(id);
    return { ok: true, messageId: id };
  }

  async respond(interactionId: string, response: InteractionResponse): Promise<boolean> {
    if (this.destroyed) return false;
    return this.interactions.respond(interactionId, response);
  }

  async retry(messageId: string): Promise<boolean> {
    const item = this.state.items.find((i): i is Message => i.kind === 'message' && i.id === messageId);
    if (!item || item.status !== 'failed' || !item.idempotencyKey || this.destroyed || this.locked) return false;
    this.dropPending(messageId);
    this.pending.set(messageId, { frame: this.frameFor(item), attempts: 0, gen: -1, ackTimer: null });   // stored key, never a new one
    this.setMessage(messageId, { status: 'sending' });
    if (!(await this.ensureSession())) {
      this.dropPending(messageId);
      this.setMessage(messageId, { status: 'failed' });
      return false;
    }
    this.transmit(messageId);
    return true;
  }

  newSession(opts: { keepHistory?: boolean } = {}): Promise<boolean> {
    if (this.destroyed) return Promise.resolve(false);
    this.locked = false;   // the server decides again on the next session request
    this.clearLimitNotice();
    // A send already provisioning the first session satisfies a concurrent New chat.
    if (!this.sessionId && this.ensureP) return this.ensureP.then((sid) => sid !== null);
    return this.serialize(async () => {
      if (this.destroyed) return false;
      if (this.sessionId) this.endSession('new_session');
      if (opts.keepHistory === false) this.patch({ items: [] });
      this.showWelcome();
      if (this.serverMode) {
        this.wantNewServerSession = true;
        const sid = await this.openServerSession();
        if (sid) await this.carryVisitor(sid);
        return sid !== null;
      }
      try {
        const created = await this.http.createSession(this.opts.agentId, getVisitorId(this.store));
        if (this.destroyed) return false;
        this.adopt(created.id, null, 'keep');
        await this.carryVisitor(created.id);
        return true;
      } catch (e) {
        this.fail(e, false);
        return false;
      }
    });
  }

  subscribe(listener: (state: ChatState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getState(): ChatState { return this.state; }

  resolveUrl(path: string): string { return this.http.resolveUrl(path); }

  // ---------- server extras ----------

  /** An older or flag-off server sends none of the keys: leave the state exactly as it was. */
  private extrasPatch(boot: unknown): Partial<ChatState> {
    const extras = parseExtras(boot);
    return extras ? { extras } : {};
  }

  private get visitorKey(): string { return `osvi.chat.prechat.${this.opts.agentId}`; }

  private readVisitor(): VisitorInfo | undefined {
    try {
      const raw = this.store.get(this.visitorKey); if (!raw) return undefined;
      return {};   // only a "submitted" flag is stored; the details themselves are never kept
    } catch { return undefined; }
  }

  /** A New chat is a new server session: it gets the same pre-chat details, or the form returns when they are not in memory. */
  private async carryVisitor(sessionId: string): Promise<void> {
    const visitor = this.state.visitor;
    if (!visitor) return;
    if (visitor.name || visitor.phone) {
      try { await this.http.sendVisitor(sessionId, visitor); return; } catch { /* fall through: ask again */ }
    }
    if (this.destroyed) return;
    this.store.remove(this.visitorKey);
    this.patch({ visitor: undefined });
  }

  async submitPrechat(info: VisitorInfo): Promise<PrechatOutcome> {
    const visitor: VisitorInfo = {};
    if (info.name?.trim()) visitor.name = info.name.trim();
    if (info.phone?.trim()) visitor.phone = info.phone.trim();
    try {
      const sid = await this.ensureSession();
      if (!sid || this.destroyed) return { ok: false, reason: 'unavailable' };
      await this.http.sendVisitor(sid, visitor);
    } catch (e) {
      const code = e instanceof ChatRequestError ? e.error.code : undefined;
      return { ok: false, reason: code === 'visitor_invalid' ? 'invalid' : code === 'rate_limited' ? 'rate_limited' : code === 'network' ? 'network' : 'unavailable' };
    }
    if (this.destroyed) return { ok: false, reason: 'unavailable' };
    this.store.set(this.visitorKey, '1');
    this.patch({ visitor });
    return { ok: true };
  }

  async whatsappHandoff(): Promise<HandoffOutcome> {
    try {
      const sid = await this.ensureSession();
      if (!sid) return { ok: false, reason: 'unavailable' };
      const { url } = await this.http.whatsappHandoff(sid);
      return isWhatsAppUrl(url) ? { ok: true, url } : { ok: false, reason: 'unavailable' };
    } catch (e) {
      if (e instanceof ChatRequestError) {
        if (e.error.code === 'rate_limited') return { ok: false, reason: 'rate_limited' };
        if (e.error.code === 'network') return { ok: false, reason: 'network' };
      }
      return { ok: false, reason: 'unavailable' };
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.sessionGeneration += 1;
    this.conn?.stop();
    this.conn = null;
    this.timers.forEach(clearTimeout);
    this.timers.clear();
    this.pending.forEach((e) => { if (e.ackTimer) clearTimeout(e.ackTimer); });
    this.pending.clear();
    if (this.awaitingTimer) clearTimeout(this.awaitingTimer);
    if (this.supportTypingTimer) clearTimeout(this.supportTypingTimer);
    this.interactions.reset();
    this.sessionId = null;
    this.latestToken = null;
    this.switchTo = null;
    this.state = emptyState();   // drops every in-memory item; listeners are not notified
    this.listeners.clear();
  }

  // ---------- sessions ----------

  private ensureSession(): Promise<string | null> {
    if (this.ensureP) return this.ensureP;
    this.ensureP = this.serialize(async () => {
      if (this.destroyed) return null;
      if (this.sessionId) return this.sessionId;
      return this.serverMode ? this.openServerSession() : this.openAnonymousSession();
    }).finally(() => { this.ensureP = null; });
    return this.ensureP;
  }

  private async openAnonymousSession(): Promise<string | null> {
    try {
      const visitor = getVisitorId(this.store);
      const boot = await this.http.bootstrap(this.opts.agentId, visitor);
      if (this.destroyed) return null;
      this.patch({ agent: { agentId: this.opts.agentId, name: boot.agent?.name ?? '' }, ...this.extrasPatch(boot) });
      this.applyWelcomeConfig(boot.agent?.welcome);
      if (boot.session?.id && boot.session.status === 'active') {
        this.adopt(boot.session.id, boot, 'append');
        return this.sessionId;
      }
      const created = await this.http.createSession(this.opts.agentId, visitor);
      if (this.destroyed) return null;
      this.adopt(created.id, null, 'keep');
      return this.sessionId;
    } catch (e) {
      this.fail(e, false);
      return null;
    }
  }

  private async openServerSession(given?: ServerToken): Promise<string | null> {
    const generation = this.sessionGeneration;
    try {
      let first = given ? this.accept(given) : await this.serverToken(generation);
      this.assertCurrent(generation);
      this.latestToken = first.token;
      let snap: Snapshot;
      try { snap = await this.http.snapshot(first.sessionId, first.token); }
      catch (e) {
        this.assertCurrent(generation);
        if (!(e instanceof ChatRequestError) || e.error.code !== 'invalid_token') throw e;
        first = await this.serverToken(generation);
        this.assertCurrent(generation);
        this.latestToken = first.token;
        snap = await this.http.snapshot(first.sessionId, first.token);
      }
      this.assertCurrent(generation);
      if (snap.agent) {
        this.patch({ agent: { agentId: this.opts.agentId, name: typeof snap.agent.name === 'string' ? snap.agent.name : '' } });
        this.applyWelcomeConfig(snap.agent.welcome);
      }
      this.adopt(first.sessionId, snap, this.state.items.length ? 'append' : 'replace', first);
      return this.sessionId;
    } catch (e) {
      if (this.isCurrent(generation)) this.fail(e, true);
      return null;
    }
  }

  private async serverToken(generation = this.sessionGeneration): Promise<ServerToken> {
    this.assertCurrent(generation);
    if (this.opts.auth?.mode !== 'server') throw new Error('not server mode');
    let t: ServerToken;
    const fresh = this.wantNewServerSession;
    this.wantNewServerSession = false;
    try { t = await (fresh ? this.opts.auth.getToken({ newSession: true }) : this.opts.auth.getToken()); }
    catch { throw new ChatRequestError(toChatError('token_failed', 'getToken failed'), 0); }
    this.assertCurrent(generation);
    try { return this.accept(t); }
    catch (e) { if (this.isCurrent(generation)) this.fail(e, false); throw e; }
  }

  private accept(t: ServerToken): ServerToken {
    this.checkWsUrl(t.wsUrl);
    return { ...t, expiresAt: normalizeExpiry(t.expiresAt) };
  }

  // wss: only (ws: for localhost during development); anything else is a token failure.
  private checkWsUrl(url: string): void {
    let u: URL | null = null;
    try { u = new URL(url); } catch { /* invalid */ }
    const local = u?.hostname === 'localhost' || u?.hostname === '127.0.0.1';
    if (u && (u.protocol === 'wss:' || (u.protocol === 'ws:' && local))) return;
    const error = toChatError('token_failed', 'The chat stream URL must use wss://');
    throw new ChatRequestError(error, 0);
  }

  private isCurrent(generation: number, sessionId?: string): boolean {
    return !this.destroyed && this.sessionGeneration === generation &&
      (sessionId === undefined || this.sessionId === sessionId);
  }

  private assertCurrent(generation: number, sessionId?: string): void {
    if (!this.isCurrent(generation, sessionId)) throw new ChatRequestError(toChatError('session_closed'), 0);
  }

  /** Bind to a session; `firstToken` lets server mode reuse the token it just fetched. */
  private adopt(sessionId: string, snapshot: Snapshot | null, merge: 'replace' | 'append' | 'keep', firstToken?: ServerToken, allowInitialReplacement = true): void {
    if (this.sessionId === sessionId && this.conn) return;   // already bound: never open a second socket
    this.conn?.stop();
    this.conn = null;
    this.sessionId = sessionId;
    const generation = ++this.sessionGeneration;
    if (!this.serverMode) this.store.set(sessionHintKey(this.opts.agentId), sessionId);
    if (snapshot) this.applySnapshot(snapshot, merge);
    let reuse = firstToken ?? null;
    const visitor = this.serverMode ? null : getVisitorId(this.store);
    let initialMint = true;
    let replaceInitialSession = false;
    const mint = async (): Promise<MintedToken> => {
      this.assertCurrent(generation, sessionId);
      if (reuse) { const t = reuse; reuse = null; this.latestToken = t.token; return t; }
      if (this.serverMode) {
        const t = await this.serverToken(generation);
        this.assertCurrent(generation, sessionId);
        if (t.sessionId !== sessionId) {   // the host's backend moved this user to another session
          this.switchTo = t;
          throw new ChatRequestError(toChatError('session_closed'), 0);
        }
        this.latestToken = t.token;
        return t;
      }
      const firstMint = initialMint;
      initialMint = false;
      let t: MintedToken;
      try { t = await this.http.mintPublicToken(sessionId); }
      catch (e) {
        replaceInitialSession = firstMint && allowInitialReplacement && this.isCurrent(generation, sessionId) &&
          e instanceof ChatRequestError && e.status === 404 && e.error.code === 'session_not_found' &&
          [...this.pending.values()].every((entry) => entry.gen === -1);
        throw e;
      }
      this.assertCurrent(generation, sessionId);
      try { this.checkWsUrl(t.wsUrl); }
      catch (e) { this.fail(e, false); throw e; }
      this.latestToken = t.token;
      return t;
    };
    this.conn = new StreamConnection({
      mint, sdkVersion: SDK_VERSION, page: this.page, random: this.opts.random, WebSocketImpl: this.opts.WebSocketImpl,
      onFrame: (env) => this.onFrame(env),
      onStatus: (status) => this.patch({ status, ...(status === 'open' && !STICKY_ON_RECONNECT.has(this.state.error?.code ?? '') ? { error: null } : {}) }),
      onOpen: () => this.onOpen(),
      onReconnected: () => void this.recover(),
      onError: (error) => this.patch({ error }),
      onClosed: (reason) => {
        if (!this.isCurrent(generation, sessionId)) return;
        if (reason === 'public_chat_disabled') { this.lock(); return; }
        if (replaceInitialSession && visitor && reason === 'session_not_found') {
          replaceInitialSession = false;
          void this.serialize(() => this.replaceBeforeFirstWrite(generation, sessionId, visitor));
          return;
        }
        const next = this.switchTo;
        this.switchTo = null;
        this.endSession(reason);
        if (next) void this.serialize(() => this.openServerSession(next));
      },
    });
    this.conn.start();
  }

  /** An initial mint can lose to a sibling tab before this tab has written anything. */
  private async replaceBeforeFirstWrite(generation: number, predecessor: string, visitor: string): Promise<void> {
    if (!this.isCurrent(generation, predecessor)) return;
    try {
      const boot = await this.http.bootstrap(this.opts.agentId, visitor);
      if (!this.isCurrent(generation, predecessor)) return;
      const session = boot?.session;
      const identity = session as unknown as Record<string, unknown> | null;
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (boot?.agent?.agent_uuid !== this.opts.agentId || !Array.isArray(boot.messages) ||
          !session || typeof session.id !== 'string' || !uuidPattern.test(session.id) ||
          session.id === predecessor || session.status !== 'active' ||
          (session.mode !== 'ai' && session.mode !== 'support') ||
          (identity && 'customer_id' in identity && identity.customer_id !== visitor) ||
          (identity && 'agent_uuid' in identity && identity.agent_uuid !== this.opts.agentId) ||
          [...this.pending.values()].some((entry) => entry.gen !== -1)) {
        this.endSession('session_not_found');
        return;
      }
      // Keep the original pending frames and forbid another replacement on its initial mint.
      this.adopt(session.id, boot, 'append', undefined, false);
    } catch {
      if (this.isCurrent(generation, predecessor)) this.endSession('session_not_found');
    }
  }

  // Every socket that becomes current (first open, reconnect, rotation): resend what is unacked.
  private onOpen(): void {
    this.openGen += 1;
    this.seen = [];
    this.seenSet.clear();
    for (const id of [...this.pending.keys()]) this.write(id);
    this.interactions.onConnectionOpen();
  }

  private async recover(): Promise<void> {
    const sid = this.sessionId;
    const generation = this.sessionGeneration;
    const snap = await this.fetchSnapshot();
    if (snap && sid && this.isCurrent(generation, sid)) this.applySnapshot(snap, 'reconcile');
  }

  private async fetchSnapshot(): Promise<Snapshot | null> {
    if (this.destroyed) return null;
    const sid = this.sessionId;
    const generation = this.sessionGeneration;
    if (!sid) return null;
    try {
      try {
        const snapshot = await this.http.snapshot(sid, this.latestToken ?? (await this.mintFresh(sid)));
        return this.isCurrent(generation, sid) ? snapshot : null;
      } catch (e) {
        this.assertCurrent(generation, sid);
        if (!(e instanceof ChatRequestError) || e.error.code !== 'invalid_token') throw e;
        const snapshot = await this.http.snapshot(sid, await this.mintFresh(sid));   // mint once, retry once
        return this.isCurrent(generation, sid) ? snapshot : null;
      }
    } catch (e) {
      if (e instanceof ChatRequestError && e.error.code === 'session_not_found') {
        if (!this.isCurrent(generation, sid)) return null;
        this.endSession('session_closed');   // not an error to show: a sibling tab may have replaced it
        if (!this.serverMode) void this.readoptAfterReplacement();
      }
      return null;
    }
  }

  private async mintFresh(sessionId: string): Promise<string> {
    const generation = this.sessionGeneration;
    this.assertCurrent(generation, sessionId);
    if (this.serverMode) {
      const t = await this.serverToken(generation);
      this.assertCurrent(generation, sessionId);
      this.latestToken = t.token;
      return t.token;
    }
    const t = await this.http.mintPublicToken(sessionId);
    this.assertCurrent(generation, sessionId);
    this.checkWsUrl(t.wsUrl);
    this.latestToken = t.token;
    return t.token;
  }

  private async readoptAfterReplacement(): Promise<void> {
    try {
      const boot = await this.http.bootstrap(this.opts.agentId, getVisitorId(this.store));
      if (!this.destroyed && !this.sessionId && boot.session?.id && boot.session.status === 'active') {
        this.adopt(boot.session.id, boot, 'append');
      }
    } catch { /* stays closed; next send re-bootstraps */ }
  }

  private endSession(reason: string): void {
    if (this.destroyed) return;
    this.sessionGeneration += 1;
    this.conn?.stop();
    this.conn = null;
    this.sessionId = null;
    this.setSupportTyping(false);
    this.latestToken = null;
    if (!this.serverMode) this.store.remove(sessionHintKey(this.opts.agentId));
    this.interactions.reset();
    this.pending.forEach((e) => { if (e.ackTimer) clearTimeout(e.ackTimer); });
    this.pending.clear();
    // Every send still in flight has an unknown outcome: failed, and retry() reuses its key.
    const settled = this.state.items.map((i) => (i.kind === 'message' && i.status === 'sending' ? { ...i, status: 'failed' as const } : i));
    const last = settled[settled.length - 1];
    const divider: Divider = { kind: 'divider', id: uuid(), reason: 'session_ended' };
    this.setAwaiting(false);
    this.patch({
      status: 'closed', mode: 'ai',
      items: settled.length && last?.kind !== 'divider' ? [...settled, divider] : settled,
      ...(QUIET_END_REASONS.has(reason) ? (this.limitNoticeShown() ? { error: null } : {}) : { error: toChatError(reason) }),
    });
  }

  // ---------- frames ----------

  private onFrame(env: WireEnvelope): void {
    if (this.destroyed) return;
    if (env.event_id !== undefined) {
      const key = String(env.event_id);
      if (this.seenSet.has(key)) return;
      this.seenSet.add(key);
      this.seen.push(key);
      if (this.seen.length > SEEN_EVENT_CAP) this.seenSet.delete(this.seen.shift()!);
    }
    const p = env.payload;
    switch (env.type) {
      case 'ws_write_result':
        if (!this.interactions.handleWriteResult(p as unknown as WriteResult)) this.onWriteResult(p as unknown as WriteResult);
        return;
      case 'message_received': {
        if (p.type === 'interaction') return;
        const msg = readWireMessage(p);
        if (!msg) return;
        if (msg.role === 'user') this.onUserEcho(msg.id, msg.content, msg.created_at, typeof p.message_id === 'string' ? p.message_id : undefined);
        else this.appendRemote(msg.id, msg.role, msg.content, msg.created_at);
        return;
      }
      case 'operator_message': {
        const msg = readWireMessage({ ...p, role: 'operator' });
        if (msg) this.appendRemote(msg.id, 'operator', msg.content, msg.created_at);
        return;
      }
      case 'support.typing': this.setSupportTyping(true); return;
      case 'reaction': this.onReaction(p, env.ts); return;
      case 'mode_changed':
        if (p.to === 'support') this.setMode('human');
        else if (p.to === 'ai') this.setMode('ai');
        return;
      case 'takeover_started': this.setMode('human'); return;
      case 'takeover_ended': this.setMode('ai'); return;
      case 'interaction_published':
      case 'interaction_updated':
      case 'interaction_closed':
        if (this.interactions.applyEvent(env) && env.type === 'interaction_published') this.setAwaiting(false);
        this.patch({});
        return;
      case 'turn_result':
        if (p.state === 'failed' || p.state === 'error') {
          this.setAwaiting(false);
          this.patch({ error: { code: 'unknown', message: 'The assistant could not reply', retryable: true } });
        } else if (p.state === 'ok' && this.isLatestUserMessage(p.message_id)) {
          // A finished turn may send no text (a reaction-only reply, or a deliberate silence).
          this.setAwaiting(false);
        }
        return;
      case 'session_closed':
      case 'session_dormant':
        this.endSession(env.type);
        return;
      default:
        return;
    }
  }

  private onWriteResult(r: WriteResult): void {
    const id = r.message_id;
    if (!id) return;
    const entry = this.pending.get(id);
    if (!entry) return;
    if (entry.ackTimer) { clearTimeout(entry.ackTimer); entry.ackTimer = null; }
    if (r.state === 'accepted' || r.state === 'merged' || r.state === 'done') {
      this.pending.delete(id);
      this.setMessage(id, { status: 'sent' });
      this.setAwaiting(true);
      this.clearLimitNotice();
      return;
    }
    if (r.state === 'invalid' && this.onLimitRejection(id, entry, r)) return;
    if (r.state === 'busy' && entry.attempts < BUSY_DELAYS_MS.length) {
      const delay = BUSY_DELAYS_MS[entry.attempts]!;
      entry.attempts += 1;
      this.later(delay, () => {
        if (this.pending.get(id) !== entry) return;
        entry.gen = -1;
        this.transmit(id);
      });
      return;
    }
    this.pending.delete(id);
    this.setMessage(id, { status: 'failed' });
    this.patch({ error: toChatError(r.error, r.error) });
  }

  // Engine rejection of a write (anonymous limits, switched-off chat). Returns whether it handled the result.
  private onLimitRejection(id: string, entry: PendingSend, r: WriteResult): boolean {
    const given = Number(r.retry_after_s);
    const seconds = (fallback: number) => (Number.isFinite(given) && given > 0 ? Math.ceil(given) : fallback);
    if (r.error === 'rate_limited' && (entry.limited ?? 0) < MAX_LIMIT_RETRIES) {
      const wait = seconds(DEFAULT_RATE_WAIT_S);
      entry.limited = (entry.limited ?? 0) + 1;
      this.showLimit('rate_limited', wait);
      this.later(wait * 1000, () => {
        this.clearLimitNotice('rate_limited');
        if (this.pending.get(id) !== entry) return;
        entry.gen = -1;
        this.transmit(id);
      });
      return true;
    }
    if (r.error === 'daily_limit_reached') {
      this.pending.delete(id);
      this.setMessage(id, { status: 'failed', failReason: 'daily_limit_reached' });
      this.showLimit('daily_limit_reached', seconds(DEFAULT_DAILY_WAIT_S));
      return true;
    }
    if (r.error === 'session_limit_reached') {
      this.pending.delete(id);
      this.setMessage(id, { status: 'failed', failReason: 'session_limit_reached' });
      this.showLimit('session_limit_reached');
      return true;
    }
    if (r.error === 'public_chat_disabled') {
      this.pending.delete(id);
      this.setMessage(id, { status: 'failed', failReason: 'public_chat_disabled' });
      this.lock();
      return true;
    }
    return false;
  }

  private limitNoticeShown(): boolean { return ['rate_limited', 'daily_limit_reached', 'session_limit_reached'].includes(this.state.error?.code ?? ''); }
  /** Drop a countdown/busy notice that no longer describes anything in flight. */
  private clearLimitNotice(only?: 'rate_limited'): void {
    const code = this.state.error?.code;
    if (code === 'rate_limited' || (!only && (code === 'daily_limit_reached' || code === 'session_limit_reached'))) this.patch({ error: null });
  }

  /** Surface an engine limit to the visitor; shared by message writes and interaction responses. */
  private showLimit(code: 'rate_limited' | 'daily_limit_reached' | 'session_limit_reached' | 'public_chat_disabled', given?: number): void {
    if (code === 'public_chat_disabled') { this.lock(); return; }
    // Lasts until New chat: this session will never accept another message.
    if (code === 'session_limit_reached') { this.patch({ error: toChatError(code) }); return; }
    const withWait = (wait: number, retryable: boolean): ChatError =>
      ({ ...toChatError(code), retryable, retryAfterSeconds: wait, retryAt: Date.now() + wait * 1000 });
    if (code === 'rate_limited') {
      this.patch({ error: given === undefined ? toChatError(code) : withWait(given, true) });
      return;
    }
    const wait = Math.min(given ?? DEFAULT_DAILY_WAIT_S, DEFAULT_DAILY_WAIT_S);
    this.patch({ error: withWait(wait, false) });
    this.later(wait * 1000, () => { if (this.state.error?.code === 'daily_limit_reached') this.patch({ error: null }); });
  }

  /** Public chat was switched off: stop the stream, keep the transcript, session hint and visitor. */
  private lock(): void {
    if (this.destroyed) return;
    this.locked = true;
    this.conn?.stop();
    this.conn = null;
    this.pending.forEach((e) => { if (e.ackTimer) clearTimeout(e.ackTimer); });
    this.pending.clear();
    this.setAwaiting(false);
    this.patch({ status: 'closed', error: toChatError('public_chat_disabled') });
  }

  /** Our own message comes back as a user echo: adopt it (by client message_id, else by content on an older engine). */
  private onUserEcho(serverId: string, content: string, createdAt?: string, clientId?: string): void {
    const items = this.state.items;
    if (items.some((i) => i.kind === 'message' && i.serverId === serverId)) return;
    const own = clientId
      ? items.findIndex((i) => i.kind === 'message' && i.role === 'user' && i.id === clientId)
      : items.findIndex((i) => i.kind === 'message' && i.role === 'user' && !i.serverId && i.status !== 'failed' && i.content === content);
    if (own >= 0) {
      const next = items.slice();
      const mine = next[own] as Message;
      next[own] = { ...mine, serverId, status: 'sent' };
      this.dropPending(mine.id);
      this.patch({ items: next });
      return;
    }
    this.appendRemote(serverId, 'user', content, createdAt);
  }

  /** One reaction per `by` on the message with this server id; an empty emoji removes it. */
  private isLatestUserMessage(id: unknown): boolean {
    if (typeof id !== 'string' || !id) return false;
    const users = this.state.items.filter((i): i is Message => i.kind === 'message' && i.role === 'user');
    return users.at(-1)?.id === id;
  }

  private onReaction(p: Record<string, unknown>, ts?: string): void {
    const by = p.by;
    if ((by !== 'agent' && by !== 'customer') || typeof p.emoji !== 'string') return;
    const who: Reaction['by'] = by;
    if (typeof p.message_id !== 'string' && typeof p.message_id !== 'number') return;
    const serverId = String(p.message_id);
    const at = this.state.items.findIndex((i) => i.kind === 'message' && i.serverId === serverId);
    if (at < 0) return;
    const msg = this.state.items[at] as Message;
    const kept = (msg.reactions ?? []).filter((r) => r.by !== who);
    const reactions: Reaction[] = p.emoji ? [...kept, { emoji: p.emoji, by: who, at: ts ?? new Date().toISOString() }] : kept;
    const { reactions: _old, ...rest } = msg;
    const items = this.state.items.slice();
    items[at] = reactions.length ? { ...rest, reactions } : rest;
    this.patch({ items });
  }

  private appendRemote(serverId: string, wireRole: string, content: string, createdAt?: string): void {
    const role = ROLE_MAP[wireRole];
    if (!role || (role !== 'user' && !content.trim())) return;   // e.g. the saved copy of a welcome that had no text
    if (this.state.items.some((i) => i.kind === 'message' && i.serverId === serverId)) return;
    const item: Message = { kind: 'message', id: `srv:${serverId}`, serverId, role, content,
      createdAt: createdAt ?? new Date().toISOString(), status: 'sent' };
    this.patch({ items: [...this.state.items, item] });
    if (role !== 'user') { this.setAwaiting(false); this.setSupportTyping(false); }
  }

  // ---------- snapshot → items ----------

  private applySnapshot(snapshot: Snapshot, merge: 'replace' | 'append' | 'keep' | 'reconcile'): void {
    if (this.destroyed) return;
    const incoming: TranscriptItem[] = [];
    const clientIds = new Map<string, string>();   // serverId -> client message_id, when the snapshot carries it
    for (const raw of snapshot.messages ?? []) {
      const offer = readInteraction(raw);
      if (offer) { incoming.push({ kind: 'interaction', id: offer.interaction_id, interaction: offer }); continue; }
      const m = readWireMessage(raw);
      const role = m ? ROLE_MAP[m.role] : undefined;
      if (m && role && (role === 'user' || m.content.trim())) {
        incoming.push({ kind: 'message', id: `srv:${m.id}`, serverId: m.id, role, content: m.content,
          createdAt: m.created_at ?? new Date().toISOString(), status: 'sent', ...(m.reactions ? { reactions: m.reactions } : {}) });
        const cid = (raw as Record<string, unknown>).message_id;
        if (typeof cid === 'string') clientIds.set(m.id, cid);
      }
    }
    let items = this.state.items;
    const welcomeAt = items.findIndex((i) => i.kind === 'welcome');
    const hadWelcome = welcomeAt >= 0;
    if (incoming.length) items = items.filter((i) => i.kind !== 'welcome');   // the persisted copy is the truth
    if (merge === 'replace') items = incoming;
    else if (merge === 'append') {
      // New session history goes after what is shown (endSession already added the divider)
      // but before this tab's still-unsent messages, which belong to the new session.
      let cut = items.length;
      while (cut > 0) {
        const i = items[cut - 1]!;
        if (i.kind === 'message' && i.role === 'user' && i.status === 'sending' && !i.serverId) cut -= 1;
        else break;
      }
      items = [...items.slice(0, cut), ...incoming, ...items.slice(cut)];
    } else if (merge === 'reconcile') {
      const known = new Set(items.flatMap((i) => (i.kind === 'message' ? [i.serverId] : i.kind === 'interaction' ? [i.id] : [])));
      let front = Math.max(welcomeAt, 0), seenUser = false;   // rows before the visitor's first message (the welcome) go where the local welcome stood
      for (const item of incoming) {
        if (item.kind === 'message' && item.role === 'user') seenUser = true;
        const key = item.kind === 'message' ? item.serverId : item.id;
        if (item.kind === 'message' && item.role === 'user' && !known.has(key)) {
          const cid = clientIds.get(item.serverId!);
          const own = items.findIndex((i) => i.kind === 'message' && i.role === 'user' && !i.serverId &&
            (cid ? i.id === cid : i.content === item.content));
          if (own >= 0) {
            const mine = items[own] as Message;
            items = items.slice();
            items[own] = { ...mine, serverId: item.serverId, status: 'sent' };
            this.dropPending(mine.id);
            known.add(key);
            continue;
          }
        }
        if (!known.has(key)) {
          if (hadWelcome && !seenUser) { items = [...items.slice(0, front), item, ...items.slice(front)]; front += 1; }
          else items = [...items, item];
          known.add(key);
        }
      }
    }
    const session = snapshot.session;
    this.patch({ items, ...(session ? { mode: session.mode === 'support' ? 'human' : 'ai' } : {}) });
    this.interactions.applySnapshot(snapshot);
    if (!incoming.length) this.showWelcome();
    const last = this.state.items.filter((i) => i.kind !== 'divider' && i.kind !== 'welcome').at(-1);
    if (last && !(last.kind === 'message' && last.role === 'user')) this.setAwaiting(false);
    this.patch({});
  }

  // ---------- welcome ----------

  /** Adopt the agent's current welcome config; an already shown welcome is updated or removed in place. */
  private applyWelcomeConfig(raw: unknown): void {
    this.welcome = readWelcome(raw);
    const w = this.welcome;
    if (!this.state.items.some((i) => i.kind === 'welcome')) return;
    this.patch({ items: w
      ? this.state.items.map((i) => (i.kind === 'welcome' ? { kind: 'welcome' as const, id: 'welcome' as const, ...w } : i))
      : this.state.items.filter((i) => i.kind !== 'welcome') });
  }

  /** Show the welcome at the end of the transcript unless a conversation is already under way after the last divider. */
  private showWelcome(): void {
    const w = this.welcome;
    if (!w || this.destroyed) return;
    const items = this.state.items;
    const lastDivider = items.map((i) => i.kind).lastIndexOf('divider');
    if (items.slice(lastDivider + 1).some((i) => i.kind === 'message' || i.kind === 'interaction')) return;
    this.patch({ items: [...items.filter((i) => i.kind !== 'welcome'), { kind: 'welcome', id: 'welcome', ...w }] });
  }

  // ---------- sends ----------

  private frameFor(m: Message): Record<string, unknown> {
    return { type: 'message', content: m.content, message_id: m.id, idempotency_key: m.idempotencyKey,
      supported_interactions: SUPPORTED_INTERACTIONS };
  }

  private transmit(id: string): void {
    this.write(id);   // when the socket is not open the entry waits in `pending`; onOpen() writes it
  }

  // Write once per socket generation; an ack timer starts with every write.
  private write(id: string): void {
    const entry = this.pending.get(id);
    if (!entry || !this.conn?.isOpen || entry.gen === this.openGen) return;
    if (!this.conn.send(entry.frame)) return;
    entry.gen = this.openGen;
    if (entry.ackTimer) clearTimeout(entry.ackTimer);
    entry.ackTimer = setTimeout(() => void this.onAckTimeout(id), SEND_ACK_TIMEOUT_MS);
  }

  // No ack within 15 s: reconcile with the server; if the message is still unconfirmed, it failed (retry reuses the key).
  private async onAckTimeout(id: string): Promise<void> {
    const entry = this.pending.get(id);
    if (!entry || this.destroyed) return;
    entry.ackTimer = null;
    if (!this.conn?.isOpen) return;   // a reopen will resend and re-arm
    const generation = this.openGen;
    await this.recover();
    if (this.destroyed || this.pending.get(id) !== entry || !this.conn?.isOpen || generation !== this.openGen) return;
    this.dropPending(id);
    this.setMessage(id, { status: 'failed' });
    this.patch({ error: toChatError('network', 'The message could not be confirmed') });
  }

  private dropPending(id: string): void {
    const entry = this.pending.get(id);
    if (entry?.ackTimer) clearTimeout(entry.ackTimer);
    this.pending.delete(id);
  }

  // ---------- helpers ----------

  private setMode(mode: 'ai' | 'human'): void {
    this.setAwaiting(false);
    this.patch({ mode });
  }

  private setAwaiting(on: boolean): void {
    if (this.awaitingTimer) { clearTimeout(this.awaitingTimer); this.awaitingTimer = null; }
    if (on) this.awaitingTimer = setTimeout(() => { this.awaitingTimer = null; this.patch({ awaitingReply: false }); }, AWAITING_REPLY_TIMEOUT_MS);
    if (this.state.awaitingReply !== on) this.patch({ awaitingReply: on });
  }

  private setSupportTyping(on: boolean): void {
    if (this.supportTypingTimer) { clearTimeout(this.supportTypingTimer); this.supportTypingTimer = null; }
    if (on) this.supportTypingTimer = setTimeout(() => { this.supportTypingTimer = null; this.patch({ supportTyping: false }); }, SUPPORT_TYPING_MS);
    if (!!this.state.supportTyping !== on) this.patch({ supportTyping: on });
  }

  private setMessage(id: string, change: Partial<Message>): void {
    this.patch({ items: this.state.items.map((i) => (i.kind === 'message' && i.id === id ? { ...i, ...change } : i)) });
  }

  private fail(e: unknown, terminal: boolean): void {
    const error = e instanceof ChatRequestError ? e.error : toChatError('network', 'Network error');
    this.patch({ error, ...(terminal || !error.retryable ? { status: 'closed' as const } : {}) });
  }

  private later(ms: number, fn: () => void): void {
    const t = setTimeout(() => { this.timers.delete(t); if (!this.destroyed) fn(); }, ms);
    this.timers.add(t);
  }

  private patch(change: Partial<ChatState>): void {
    if (this.destroyed) return;
    const next: ChatState = {
      ...this.state, ...change,
      activeInteraction: this.interactions.active,
      interactionPending: this.interactions.pending,
    };
    const changed = (Object.keys(next) as (keyof ChatState)[]).some((k) => next[k] !== this.state[k]);
    if (!changed) return;
    this.state = next;
    this.listeners.forEach((l) => l(next));
  }
}

export function createChatClient(options: ChatClientOptions): ChatClient {
  return new Client(options);
}
